
import React from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { formatCurrency } from '@/lib/utils';

interface InvoiceTypeSelectorProps {
  form: any;
  invoiceType: string;
  remainingBalance: number;
}

const InvoiceTypeSelector: React.FC<InvoiceTypeSelectorProps> = ({
  form,
  invoiceType,
  remainingBalance
}) => {
  return (
    <>
      <FormField
        control={form.control}
        name="invoice_type"
        render={({ field }) => (
          <FormItem className="space-y-3">
            <FormLabel>Invoice Type</FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="flex flex-col space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="full" id="full" />
                  <label htmlFor="full">Full Balance ({formatCurrency(remainingBalance)})</label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="percentage" id="percentage" />
                  <label htmlFor="percentage">Percentage of Estimate</label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="custom_amount" id="custom_amount" />
                  <label htmlFor="custom_amount">Custom Amount</label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="partial" id="partial" />
                  <label htmlFor="partial">Select Line Items</label>
                </div>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {invoiceType === 'percentage' && (
        <FormField
          control={form.control}
          name="percentage_of_estimate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Percentage (%)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  step="0.01"
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    if (rawValue === '') {
                      field.onChange(undefined);
                    } else {
                      field.onChange(parseFloat(rawValue));
                    }
                  }}
                  onBlur={(e) => {
                    const value = parseFloat(e.target.value);
                    if (isNaN(value) || value < 1) {
                      field.onChange(1);
                    } else if (value > 100) {
                      field.onChange(100);
                    } else {
                      field.onChange(Math.round(value * 100) / 100);
                    }
                  }}
                  placeholder="Enter percentage"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {invoiceType === 'custom_amount' && (
        <FormField
          control={form.control}
          name="custom_amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Custom Amount (R)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    if (rawValue === '') {
                      field.onChange(undefined);
                    } else {
                      field.onChange(parseFloat(rawValue));
                    }
                  }}
                  onBlur={(e) => {
                    const value = parseFloat(e.target.value);
                    if (isNaN(value) || value < 0) {
                      field.onChange(0);
                    } else {
                      field.onChange(Math.round(value * 100) / 100);
                    }
                  }}
                  placeholder="Enter amount"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </>
  );
};

export default InvoiceTypeSelector;
